import { useState, ReactNode } from 'react';

import clsx from 'clsx';

import type { TConfig } from '@@/interfaces';
import { helpShown } from '@@/services/analytics/auto';

import Button, { TButtonVariant } from '../Button';
import Footer from '../Footer';
import LogoWrapper from '../LogoWrapper';
import Modal from '../Modal';

import classes from './LayoutContent.module.css';
import Text from './Text';

export interface ICTAConfig {
  type?: 'submit' | 'default' | 'none';
  onNext?: () => void;
  onBack?: () => void;
  onSubmit?: (values?: any) => void;
  isNextOrSubmitDisabled?: boolean;
  nextButtonText?: string;
  nextButtonVariant?: TButtonVariant;
  cancelButtonText?: string;
  cancelButtonVariant?: TButtonVariant;
  submitButtonText?: string;
  submitButtonVariant?: TButtonVariant;
}

const defaultCTAConfigs: ICTAConfig = {
  type: 'default',
  onNext: () => {},
  onSubmit: () => {},
  isNextOrSubmitDisabled: false,
  nextButtonText: 'Next',
  nextButtonVariant: 'PRIMARY',
  cancelButtonText: 'Go back',
  cancelButtonVariant: 'OUTLINE',
  submitButtonText: 'Submit',
  submitButtonVariant: 'PRIMARY',
};

interface ILogoConfig {
  hideLogo?: boolean;
  hidePartnerLogo?: boolean;
}

const defaultLogoConfig: ILogoConfig = {
  hideLogo: false,
  hidePartnerLogo: false,
};

export type TLayoutContent = {
  children?: ReactNode;
  header: string;
  additionalHeader?: string;
  subHeader?: string;
  CTAConfig?: ICTAConfig;
  hideFooter?: boolean;
  infoModalConfig?: {
    infoText: string;
    infoTitle?: string;
  };
  headerBlock?: string;
  logoConfig?: ILogoConfig;
  overrideClasses?: {
    root?: string;
    header?: string;
    additionalHeader?: string;
    logo_root?: string;
    logo?: string;
    img_wrapper?: string;
    subheader?: string;
    content_wrapper?: string;
    children_wrapper?: string;
    button_wrapper?: string;
    footer?: string;
    info_circle?: string;
  };
  mode?: 'light' | 'default';
  layoutContentConfig?: TConfig['layoutContentConfig'];
  featureFlags: TConfig['featureFlags'];
  overrideImg?: string;
};

function LayoutContent({
  children,
  CTAConfig,
  hideFooter = false,
  header,
  headerBlock,
  infoModalConfig,
  mode = 'default',
  overrideClasses,
  subHeader = '',
  additionalHeader = '',
  logoConfig,
  layoutContentConfig,
  featureFlags,
  overrideImg,
}: TLayoutContent) {
  const { tenantCityImg } = layoutContentConfig || {};
  const { isLogoRedirectInAutoPurchaseFlowEnabled } = featureFlags;
  const [isModalOpen, setIsModalOpen] = useState(false);
  function handleCloseModal() {
    setIsModalOpen(false);
  }

  const {
    cancelButtonText,
    cancelButtonVariant,
    isNextOrSubmitDisabled,
    nextButtonText,
    nextButtonVariant,
    onBack,
    onNext,
    onSubmit,
    submitButtonText,
    submitButtonVariant,
    type,
  } = { ...defaultCTAConfigs, ...CTAConfig };

  const { hideLogo, hidePartnerLogo } = { ...defaultLogoConfig, ...logoConfig };

  const hasTwoButtons = !!(onBack && onNext);

  return (
    <>
      {headerBlock && (
        <div className={classes.header_block} data-testid="headerBlock">
          <div className={classes.header_block_text}>{headerBlock}</div>
        </div>
      )}
      {!hideLogo && (
        <LogoWrapper
          overrideClasses={{
            root: clsx(classes.logo_wrapper_root, overrideClasses?.logo_root),
            logo: overrideClasses?.logo,
            img_wrapper: clsx(
              classes.logo_img_wrapper,
              overrideClasses?.img_wrapper
            ),
          }}
          data-testid="tenant_logo"
          disableClick={!isLogoRedirectInAutoPurchaseFlowEnabled}
          hidePartnerLogo={hidePartnerLogo}
          isLight={mode === 'light'}
        />
      )}
      <div
        className={clsx(classes.root, overrideClasses?.root, {
          [classes.root_light]: mode === 'light',
          [classes.root_header_block]: !!headerBlock,
        })}
        style={{
          backgroundImage:
            mode === 'light' ? overrideImg || `url(${tenantCityImg})` : 'none',
        }}
        data-testid="layoutQuestion"
      >
        <div className={classes.wrapper}>
          <div
            className={clsx(
              classes.content_wrapper,
              overrideClasses?.content_wrapper
            )}
          >
            <Text
              variant="header"
              className={clsx(classes.header, overrideClasses?.header)}
              data-testid="header"
            >
              {header}
            </Text>
            {additionalHeader && (
              <div
                className={clsx(
                  classes.additional_header,
                  overrideClasses?.additionalHeader || ''
                )}
                data-testid="additionalHeader"
              >
                {additionalHeader}
              </div>
            )}
            {subHeader && (
              <Text
                variant="subheader"
                className={clsx(
                  overrideClasses?.subheader || '',
                  classes.sub_header
                )}
                data-testid="subheader"
              >
                <>
                  {subHeader}
                  {!!infoModalConfig && (
                    <button
                      type="button"
                      data-testid="infoIcon"
                      className={clsx(
                        'fas fa-info-circle',
                        classes.info_circle,
                        'icon_btn',
                        overrideClasses?.info_circle
                      )}
                      aria-label="View info"
                      onClick={() => {
                        helpShown?.();
                        setIsModalOpen(true);
                      }}
                    />
                  )}
                </>
              </Text>
            )}
            <div
              className={clsx(
                classes.children_wrapper,
                overrideClasses?.children_wrapper
              )}
            >
              {children}
            </div>
            {!!CTAConfig && (
              <>
                {type === 'default' ? (
                  <div
                    className={clsx(
                      classes.buttons_wrapper,
                      overrideClasses?.button_wrapper
                    )}
                  >
                    <Button
                      type="submit"
                      data-testid="nextButton"
                      variant={nextButtonVariant}
                      isFullWidth={hasTwoButtons}
                      onClick={onNext!}
                      label={nextButtonText!}
                      disabled={isNextOrSubmitDisabled}
                      overrideClasses={{ root: classes.next_button }}
                    />
                    {onBack && (
                      <Button
                        data-testid="cancelButton"
                        variant={cancelButtonVariant}
                        isFullWidth={hasTwoButtons}
                        onClick={onBack}
                        label={cancelButtonText!}
                        overrideClasses={{ root: classes.back_button }}
                      />
                    )}
                  </div>
                ) : null}
                {type === 'submit' ? (
                  <div
                    className={clsx(
                      classes.buttons_wrapper,
                      overrideClasses?.button_wrapper
                    )}
                  >
                    <Button
                      type="submit"
                      data-testid="submitButton"
                      variant={submitButtonVariant}
                      onClick={onSubmit!}
                      label={submitButtonText!}
                      disabled={isNextOrSubmitDisabled}
                      overrideClasses={{ root: classes.submit_button }}
                    />
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {hideFooter ? null : (
        <Footer
          overrideClasses={{
            root: clsx(classes.footer, overrideClasses?.footer),
            container: classes.footer_container,
          }}
        />
      )}

      {!!infoModalConfig && (
        <Modal
          isOpen={isModalOpen}
          title={infoModalConfig.infoTitle}
          data-testid="ModalInfo"
          onRequestClose={handleCloseModal}
          hasFooter
          primaryFooterButton={{
            variant: 'SECONDARY',
            onClick: handleCloseModal,
            label: 'Close',
          }}
        >
          <p className={classes.info_text}>{infoModalConfig.infoText}</p>
        </Modal>
      )}
    </>
  );
}

export default LayoutContent;
